/**
 * "Chủ thể & liên hệ" on the domain page: what "Đồng bộ từ chủ thể" changes, and which
 * verification the registrant's badge shows.
 *
 * Pure functions (no React): `npm test` covers them.
 */

import type { ContactField, ContactRole, DomainContact, DomainSyncResult, SubjectDomainContact } from '../../../api/areas/core';
import type { SubjectVerification } from '../../../api/types';

const ROLES: readonly ContactRole[] = ['REGISTRANT', 'ADMIN', 'TECH', 'BILLING'];

/** One sync call: the subject it runs for, and the domain's contacts it changes. */
export interface SyncGroup {
  subjectId: string;
  /** The subject's name as the contacts show it (null when hidden from the role). */
  name: string | null;
  roles: ContactRole[];
}

type Row = Pick<DomainContact, 'role' | 'subject_id' | 'display_name' | 'valid_until' | 'changed_fields'>;

/**
 * A sync runs for one subject and changes only the domain's contacts copied from that subject
 * (`POST /subjects/{id}/sync-domains`), so the drifted current contacts are grouped by their own
 * subject — the legal representative's admin contact syncs from the representative, not from the
 * registrant — one sync per subject, in role order, each counting only its contacts (review #23).
 */
export function syncGroups(items: readonly Row[]): SyncGroup[] {
  const out: SyncGroup[] = [];
  for (const role of ROLES) {
    for (const c of items) {
      if (c.role !== role || c.valid_until || !c.subject_id || !(c.changed_fields ?? []).length) continue;
      let group = out.find((g) => g.subjectId === c.subject_id);
      if (!group) {
        group = { subjectId: c.subject_id, name: c.display_name, roles: [] };
        out.push(group);
      }
      group.roles.push(role);
    }
  }
  return out;
}

/** What one sync did to the domain (a registrant change and a contact update can both start). */
export function syncOutcomes(results: readonly Pick<DomainSyncResult, 'outcome'>[]): DomainSyncResult['outcome'][] {
  const outcomes = [...new Set(results.map((r) => r.outcome))];
  return outcomes.length ? outcomes : ['UP_TO_DATE'];
}

type Check = Pick<SubjectVerification, 'subject_version_id' | 'verified_at'>;

/**
 * The registrant's verification the domain page shows: the latest one of the version the dossier
 * pins — another version's never stands in for it (review #25) — or, without a dossier, the latest
 * of any version. Null: not verified.
 */
export function pinnedVerification<T extends Check>(verifications: readonly T[], pinnedVersion: string | null | undefined): T | null {
  const own = verifications.filter((x) => !pinnedVersion || x.subject_version_id === pinnedVersion);
  return own.sort((a, b) => ((a.verified_at ?? '') < (b.verified_at ?? '') ? 1 : -1))[0] ?? null;
}

/** Fields that differ, and the contacts of the domain they differ for (`roles`: null when all). */
export interface ChangeGroup {
  roles: ContactRole[] | null;
  fields: ContactField[];
}

/**
 * "Thay đổi" of one domain on a subject's page: the changed fields grouped by the contacts they
 * belong to. An organisation's admin, tech and billing follow its legal representative, so after
 * another representative their name and identity number differ while the organisation's own do
 * not: those fields are named with their roles, never as the domain's (the registrant's) change.
 */
export function changeGroups(contacts: readonly Pick<SubjectDomainContact, 'role' | 'changed_fields'>[]): ChangeGroup[] {
  const out: { roles: ContactRole[]; fields: ContactField[] }[] = [];
  for (const c of contacts) {
    if (!c.changed_fields.length) continue;
    const key = c.changed_fields.join(',');
    const g = out.find((x) => x.fields.join(',') === key);
    if (g) g.roles.push(c.role);
    else out.push({ roles: [c.role], fields: [...c.changed_fields] });
  }
  return out.map((g) => ({ roles: g.roles.length === contacts.length ? null : g.roles, fields: g.fields }));
}
